import {
  execute,
  FilterConditions,
  INSERT,
  SELECT,
  UPDATE,
} from "@/utils/database_module";
import { logDebug, logError } from "@/utils/helpers";
import {
  Conflict,
  ConflictHistory,
  ConflictStatus,
  Devis,
  Order,
  OrderHistory,
  OrderStatus,
  Product,
  SpecialOrderQuote,
  SpecialProductData,
  User,
  UserSession,
  Versement,
} from "@/utils/types";
import { DELEVERED_STATUS, DELIVERED_PAID_STATUS } from "@/utils/utilLists";
import { get } from "http";
import moment from "moment";

type OrderData = Omit<
  Order,
  | "id"
  | "date"
  | "modified_date"
  | "name"
  | "product"
  | "user"
  | "history"
  | "invoices"
> & { invoices?: string };

const orders = {
  getAll: async (filter: {
    selectedStatus?: any;
    startDate?: string;
    endDate?: string;
    limit?: number;
    order?: string;
    user_id?: number;
    is_order_special?: 0 | 1 | undefined;
    currentPage?: number;
  }) => {
    let {
      selectedStatus,
      startDate,
      endDate,
      user_id,
      limit,
      currentPage,
      order,
      is_order_special,
    } = filter;

    // Default values for pagination
    if (!limit) limit = 1000000;
    if (!currentPage) currentPage = 1;

    // Fetch all users with the role 'user'
    const userQuery = `
    SELECT id, first_name, last_name, email, phone_numbers, profile_pic, token, adress 
    FROM users 
    WHERE role = 'user' 
    ORDER BY id DESC`;
    const users = (await execute(userQuery)) as User[];

    // Parse phone_numbers from JSON string to array (if needed)
    for (const user of users) {
      user.phone_numbers = typeof user.phone_numbers
        ? JSON.parse(`${user?.phone_numbers}`)
        : user.phone_numbers;
    }

    // Prepare filtering conditions for orders
    let conditions: FilterConditions = [
      { column: "is_deleted", operator: "=", value: 0 },
    ];

    if (user_id)
      conditions.push({ column: "user_id", operator: "=", value: user_id });

    if (is_order_special !== undefined && !isNaN(is_order_special))
      conditions.push({
        column: "is_special",
        operator: "=",
        value: is_order_special,
      });

    if (selectedStatus && parseInt(selectedStatus) != 0)
      conditions.push({
        column: "status",
        operator: "=",
        value: parseInt(selectedStatus),
      });

    logDebug("orders getall", conditions);

    // Fetch orders matching conditions and pagination
    const orders = (await SELECT("orders", {
      conditions,
      order: order ? order : "modified_date DESC",
      limit: !isNaN(parseInt(`${limit * currentPage}`))
        ? parseInt(`${limit * currentPage}`)
        : limit,
    })) as Order[];

    // Filter orders by startDate and endDate
    const filteredOrders = orders.filter((order) => {
      const orderDate = moment(order.date).format("YYYY-MM-DD");

      if (
        startDate &&
        !moment(orderDate).isSameOrAfter(moment(startDate).format("YYYY-MM-DD"))
      )
        return false;

      if (
        endDate &&
        !moment(orderDate).isSameOrBefore(moment(endDate).format("YYYY-MM-DD"))
      )
        return false;

      return true;
    });

    // If no orders match the filters, return empty array
    if (filteredOrders.length == 0) return [];

    // Extract relevant IDs for further querying
    const filteredOrdersIDs = filteredOrders.map((order) => order.id);
    const filteredOrdersProductIDs = filteredOrders.map(
      (order) => order.product_id
    );

    // Fetch related payments (versements)
    const payements =
      filteredOrdersIDs.length != 0
        ? ((await execute(
            `SELECT * FROM versement WHERE order_id IN (${filteredOrdersIDs.join(
              ", "
            )})`
          )) as Versement[])
        : [];

    // Fetch possible statuses for orders
    const ordersStatuses = (await SELECT("order_status", {})) as OrderStatus[];

    // Fetch products related to the filtered orders
    const products =
      filteredOrdersProductIDs.length != 0
        ? ((await execute(
            `SELECT * FROM products WHERE id IN (${filteredOrdersProductIDs.join(
              ", "
            )})`
          )) as Product[])
        : [];

    // Parse product names if stored as JSON strings
    for (const p of products) {
      p.name = typeof p.name == "string" ? JSON.parse(`${p.name}`) : p.name;
    }

    // Enrich each order with related user, product, payments, and status
    for (const order of filteredOrders) {
      order.versements = payements.filter((p) => p.order_id == order.id);
      order.user = users.find((user) => user.id == order.user_id);
      order.status = ordersStatuses.find((s) => s.id == order.status)!;
      order.product = products.find((p) => p.id === order.product_id);

      // Parse images and names if needed
      if (order.product) {
        order.product.images =
          typeof order.product.images == "string"
            ? JSON.parse(order.product.images)
            : order.product.images;
        order.product.name =
          typeof order.product.name == "string"
            ? JSON.parse(order.product.name)
            : order.product.name;
      }
    }

    // Return fully enriched and filtered orders
    return filteredOrders;
  },

  /*
   * Get a single order by ID
   */
  getOne: async (id: number) => {
    // Fetch the order with the given ID
    const [order] = (await SELECT("orders", {
      conditions: [{ column: "id", operator: "=", value: id }],
    })) as Order[];

    if (!order) throw new Error("Order not found");

    // Parse the invoices from string to object
    order.invoices = JSON.parse(`${order?.invoices}`);

    // Fetch all payments (versements) related to this order
    order.versements = (await SELECT("versement", {
      conditions: [{ column: "order_id", operator: "=", value: id }],
    })) as Versement[];

    // Fetch the user who placed the order
    [order.user] = (await SELECT("users", {
      conditions: [
        { column: "id", operator: "=", value: order.user_id },
      ] as FilterConditions,
    })) as User[];

    // Fetch all possible order statuses
    const orderStatuses = (await SELECT("order_status", {})) as OrderStatus[];

    // Replace order.status (ID) with the full status object
    order.status = orderStatuses.find(
      (status) => status.id == order.status
    ) as OrderStatus;

    // Fetch the product associated with the order
    [order.product] = (await SELECT("products", {
      conditions: [{ column: "id", operator: "=", value: order.product_id }],
    })) as Product[];

    if (order.product) {
      // Parse product images if needed
      order.product.images =
        typeof order.product.images == "string"
          ? JSON.parse(order.product.images)
          : order.product.images;

      // Parse product name if it's multilingual (e.g. JSON format)
      order.product.name =
        typeof order.product.name == "string"
          ? JSON.parse(order.product.name)
          : order.product.name;
    }

    // Fetch all conflicts linked to the order
    order.conflicts = (await SELECT("conflicts", {
      conditions: [{ column: "order_id", operator: "=", value: id }],
    })) as Conflict[];

    // Fetch all conflict statuses
    const conflictsStatuses = (await SELECT(
      "conflicts",
      {}
    )) as ConflictStatus[];

    // For each conflict, replace the status ID with the full status object
    for (const conflict of order.conflicts) {
      conflict.status = conflictsStatuses.find(
        (status) => status.id == conflict.status
      )!;
    }

    // Fetch order history (who made what changes and when)
    const history = (await SELECT("orders_history", {
      conditions: [{ column: "order_id", operator: "=", value: id }],
    })) as OrderHistory[];

    // If there's no history, just return the order now
    if (history.length == 0) return order;

    // Get unique user IDs who modified the order
    const userIds = history.map((data) => data.user_id);
    const users = (await execute(
      `SELECT first_name, last_name, role, profile_pic, email, id FROM users WHERE id IN (${userIds.join(
        ","
      )})`
    )) as User[];

    // Enrich each history record with user info and full status objects
    for (const historyItem of history) {
      // Attach the user object to the history item
      historyItem.user = users.find((user) => user.id == historyItem.user_id);

      // Replace old/new status IDs with full status objects
      if (
        historyItem.details &&
        typeof historyItem.details.new_status == "number"
      )
        historyItem.details.new_status = orderStatuses.find(
          (status) => status.id == historyItem.details.new_status
        ) as OrderStatus;

      if (
        historyItem.details &&
        typeof historyItem.details.old_status == "number"
      )
        historyItem.details.old_status = orderStatuses.find(
          (status) => status.id == historyItem.details.old_status
        ) as OrderStatus;
    }

    // Attach the full history to the order object
    order.history = history;

    // Return the fully enriched order
    return order;
  },

  /*
   * Update an order in the database
   */
  update: async (
    id: number,
    data: { order: Partial<OrderData>; history: Partial<OrderHistory> }
  ) => {
    const { order, history } = data;
    if (
      order.status == DELIVERED_PAID_STATUS ||
      order.status == DELEVERED_STATUS
    )
      order.shipping_date = moment().format("YYYY-MM-DD HH:mm:ss");

    await INSERT("orders_history", {
      order_id: id,
      user_id: history.user_id,
      action: history.action,
      details: { ...history.details, new_status: order.status },
      color: history?.color ? history.color : "#ddsfs",
    });

    return await UPDATE("orders", order, [
      { column: "id", operator: "=", value: id },
    ]);
  },

  /*
   * Insert a new order into the database
   */

  insert: async (data: SpecialProductData, session: UserSession) => {
    const orderToInsert = data.addedOrder;
    let insertedOrder: Order | null = null;

    // Check if the quote type is defined
    if (orderToInsert.is_special == undefined)
      throw new Error(
        "Unable to determine the type of the quote (`is_special` must be defined)"
      );

    // ===== CASE 1: Regular order (not special) =====
    if (orderToInsert.is_special == 0) {
      // Get the product by its ID to fetch the reference
      const [product] = (await SELECT("products", {
        columns: ["id", "reference"],
        conditions: [
          { column: "id", operator: "=", value: orderToInsert.product_id },
        ],
      })) as Product[];

      if (!product || !product.reference) throw new Error("Product not found");

      // Fetch the quote (devis) based on the product reference and quantity
      const [devis] = (await SELECT("devis", {
        columns: ["price"],
        conditions: [
          { column: "product_ref", operator: "=", value: product.reference },
          {
            column: "min_quantity",
            operator: "<=",
            value: orderToInsert.quantity!,
          },
          {
            column: "max_quantity",
            operator: ">",
            value: orderToInsert.quantity!,
          },
          { column: "is_deleted", operator: "=", value: 0 },
        ],
      })) as Devis[];

      let newOrder = orderToInsert;

      // If a quote is found, calculate prices and prepare the new order
      if (devis) {
        const total_price = devis.price * orderToInsert.quantity!;
        newOrder = {
          ...orderToInsert,
          total_price,
          unit_price: devis.price,
          status: 2, // set initial status
        };
      }

      // Insert the regular order
      insertedOrder = (await INSERT("orders", newOrder)) as Order;
    }

    // ===== CASE 2: Special order =====
    if (orderToInsert.is_special == 1) {
      const { package_info, price: userProvidedPrice } = data;
      const { length, width, height, weight } = package_info;
      const total_quantity = orderToInsert.quantity;
      let devis = 0;

      // Ensure all necessary package details are provided
      if (
        !length ||
        !width ||
        !height ||
        !weight ||
        !total_quantity ||
        !userProvidedPrice
      )
        throw new Error("Missing package info");

      // Get all available special order quotes
      const specialOrderQuotes = (await SELECT("special_order_quote", {
        conditions: [{ column: "is_deleted", operator: "=", value: 0 }],
        order: "JSON_EXTRACT(standard_quote, '$.max_product_price') + 0 ASC",
      })) as SpecialOrderQuote[];

      // Calculate volumetric weight and compare with provided weight
      let calculatedWeight = (length * width * height) / 5000;
      logDebug("weight", calculatedWeight);
      logDebug("data?.weight", weight);

      // Use the higher value between volumetric and actual weight
      calculatedWeight = Math.max(calculatedWeight, weight);

      // Determine the unit weight per product
      const unitWeight = (calculatedWeight * 1000) / total_quantity;
      logDebug("userProvidedPrice:", userProvidedPrice);
      logDebug("unitWeight:", unitWeight);
      logDebug("total_quantity:", total_quantity);

      // Fetch the current user to check their role
      const [currentUser] = (await SELECT("users", {
        conditions: [{ column: "id", operator: "=", value: session.id! }],
        columns: ["id", "is_special"],
      })) as User[];

      const is_special = currentUser?.is_special;

      // Find the appropriate quote interval based on unit weight
      const quote = specialOrderQuotes.find(
        (q) => unitWeight >= q.min_weight && unitWeight < q.max_weight
      ) as SpecialOrderQuote;

      logDebug("quote:", quote);

      // === Quote logic based on user type ===

      if (is_special == 0) {
        // Regular user

        // If the price provided by the user is less than or equal to the allowed max price in the quote
        if (quote?.standard_quote?.max_product_price >= userProvidedPrice!) {
          devis = quote?.standard_quote?.quote;
        } else {
          // Otherwise, get the next quote interval with a higher allowed price
          const foundQuote = specialOrderQuotes.find(
            (q) => q?.standard_quote?.max_product_price >= userProvidedPrice!
          );
          devis = foundQuote?.standard_quote?.quote ?? 0;
        }
      }

      if (is_special == 1) {
        // Special user

        const priceWithinRange =
          quote?.special_quote?.min_product_price <= userProvidedPrice &&
          quote?.special_quote?.max_product_price > userProvidedPrice;

        if (priceWithinRange) {
          devis = quote?.special_quote?.quote;
        } else {
          // If the price is not in the current interval, look for one where it fits
          const foundQuote = specialOrderQuotes.find(
            (q, index) =>
              index > 0 &&
              userProvidedPrice! >= q?.special_quote?.min_product_price &&
              userProvidedPrice! < q?.special_quote?.max_product_price
          );
          devis = foundQuote?.special_quote?.quote ?? 0;
        }
      }

      logDebug("devis :", devis);
      logDebug("total_price:", devis * total_quantity);

      // Insert special order with calculated unit and total prices
      insertedOrder = (await INSERT("orders", {
        ...orderToInsert,
        unit_price: devis,
        total_price:
          calculatedWeight > 20
            ? devis * total_quantity
            : devis * calculatedWeight,
      })) as Order;
    }

    // Save order creation into history
    await INSERT("orders_history", {
      order_id: insertedOrder?.id,
      user_id: data.history.user_id,
      action: data.history.action,
      color: data.history.color,
      date: moment().format("YYYY-MM-DD HH:mm:ss"),
    });

    // Return the inserted order
    return insertedOrder;
  },

  /*
   * Delete an order from the database
   */

  delete: async (id: number) => {
    console.log("delete");
    return await UPDATE("orders", { is_deleted: 1 }, [
      { column: "id", operator: "=", value: id },
    ]);
  },

  /*
   * Count the total number of orders based on the given filters
   */
  countTotal: async (filter: {
    selectedStatus?: any;
    startDate?: string;
    endDate?: string;
    order?: string;
    user_id?: number;
    is_order_special?: 0 | 1 | undefined;
  }) => {
    // Destructure filters from the input object
    let { selectedStatus, startDate, endDate, user_id, is_order_special } =
      filter;

    // Base conditions: exclude deleted orders
    let conditions: FilterConditions = [
      { column: "is_deleted", operator: "=", value: 0 },
    ];

    // Filter by user if user_id is provided
    if (user_id)
      conditions.push({ column: "user_id", operator: "=", value: user_id });

    // Filter by special orders if specified
    if (is_order_special != undefined)
      conditions.push({
        column: "is_special",
        operator: "=",
        value: is_order_special,
      });

    // Filter by selected status if not zero
    if (selectedStatus && parseInt(selectedStatus) != 0)
      conditions.push({
        column: "status",
        operator: "=",
        value: parseInt(selectedStatus),
      });

    // Fetch orders matching the conditions
    const orders = (await SELECT("orders", {
      conditions: conditions,
    })) as Order[];

    // Further filter by date range if specified
    const filteredOrders = orders.filter((order) => {
      const orderDate = moment(order.date).format("YYYY-MM-DD");

      // Check if order date is on/after the start date
      if (
        startDate &&
        !moment(orderDate).isSameOrAfter(moment(startDate).format("YYYY-MM-DD"))
      )
        return false;

      // Check if order date is on/before the end date
      if (
        endDate &&
        !moment(orderDate).isSameOrBefore(moment(endDate).format("YYYY-MM-DD"))
      )
        return false;

      return true;
    });

    // Return the number of orders that match the filters
    return filteredOrders.length;
  },

  addHistory: async (history: Omit<OrderHistory, "id" | "date">) => {
    return await INSERT("orders_history", history);
  },
};

const apiError = (statusCode: number, message: string) => {
  console.log(`---------------- [API Error ] ------------------`);
  logError(message);
  return Response.json({ error: message, code: statusCode });
};

export default orders;
